/**
 * 報工系統組件 | Production Report Component
* 作業員每日產出回報與工時記錄
* 注重使用者體驗：快速、直觀、減少輸入錯誤
 */

import { useState, useMemo } from 'react';
import { useOrderStore } from '@/store';
import { toast } from '@/components/UI/Toast';
import type { SubWorkOrder, ProductionReport } from '@/types/order';

type ReportStep = 'select' | 'report' | 'confirm';
type SelectView = 'active' | 'my' | 'completed';

export function ProductionReport() {
  const [reportStep, setReportStep] = useState<ReportStep>('select');
  const [selectedOrder, setSelectedOrder] = useState<SubWorkOrder | null>(null);
  const [selectView, setSelectView] = useState<SelectView>('active');
  const [reportData, setReportData] = useState({
    quantity: 0,
    goodQuantity: 0,
    defectQuantity: 0,
    workHours: 0,
    notes: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const subWorkOrders = useOrderStore((state) => state.subWorkOrders);
  const mainWorkOrders = useOrderStore((state) => state.mainWorkOrders);
  const orders = useOrderStore((state) => state.orders);
  const addProductionReport = useOrderStore((state) => state.addProductionReport);
  const todayReportsCount = useOrderStore((state) => state.getTodayReportsCount());
  const productionReports = useOrderStore((state) => state.productionReports);

  // 獲取進行中的工單（優先顯示）
  const activeOrders = useMemo(() => {
    return subWorkOrders.filter((order) => order.status === '進行中');
  }, [subWorkOrders]);

  // 獲取我的工單（當前登入使用者的工單，暫時用 E001 為例）
  const myOrders = useMemo(() => {
    const currentUserId = 'E001'; // TODO: 從登入狀態取得
    return subWorkOrders.filter((order) =>
      order.assignedPersons.includes(currentUserId) && order.status === '進行中'
    );
  }, [subWorkOrders]);

  // 獲取今日報工記錄
  const todayReportsList = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return productionReports.filter((report) => report.reportDate === today);
  }, [productionReports]);

  // 根據選擇的視圖獲取顯示的工單列表
  const displayOrders = useMemo(() => {
    switch (selectView) {
      case 'my':
        return myOrders;
      case 'completed':
        return []; // 今日已報工視圖顯示報工記錄，不是工單列表
      case 'active':
      default:
        return activeOrders;
    }
  }, [selectView, activeOrders, myOrders]);

  // 獲取主工單資訊
  const getMainOrder = (subOrderId: string) => {
    const subOrder = subWorkOrders.find((o) => o.id === subOrderId);
    if (!subOrder) return null;
    return mainWorkOrders.find((m) => m.id === subOrder.mainOrderId);
  };

  // 處理工單選擇
  const handleSelectOrder = (order: SubWorkOrder) => {
    setSelectedOrder(order);
    setReportData({
      quantity: 0,
      goodQuantity: 0,
      defectQuantity: 0,
      workHours: 8, // 預設 8 小時
      notes: '',
    });
    setReportStep('report');
  };

  // 處理數量變更（自動計算良品數）
  const handleQuantityChange = (field: 'quantity' | 'goodQuantity' | 'defectQuantity', value: number) => {
    const newData = { ...reportData, [field]: value };

    // 自動計算：總數 = 良品 + 不良品
    if (field === 'goodQuantity' || field === 'defectQuantity') {
      newData.quantity = newData.goodQuantity + newData.defectQuantity;
    } else if (field === 'quantity') {
      // 如果調整總數，按比例調整良品與不良品
      const total = newData.goodQuantity + newData.defectQuantity;
      if (total > 0) {
        newData.goodQuantity = Math.round((newData.goodQuantity / total) * value);
        newData.defectQuantity = value - newData.goodQuantity;
      } else {
        newData.goodQuantity = value;
      }
    }

    setReportData(newData);
  };

  // 提交報工
  const handleSubmitReport = () => {
    if (!selectedOrder) return;

    // 表單驗證
    if (reportData.quantity === 0) {
      toast.error('請輸入產出數量', '總產出數量不能為 0');
      return;
    }

    if (reportData.workHours <= 0) {
      toast.error('請輸入工時', '工作時間必須大於 0');
      return;
    }

    // 檢查數據合理性
    if (reportData.goodQuantity + reportData.defectQuantity !== reportData.quantity) {
      toast.error('數據不一致', '良品數量 + 不良品數量應等於總產出');
      return;
    }

    // 計算良率和達成率
    const yieldRate = (reportData.goodQuantity / reportData.quantity) * 100;
    const achievementRate = (reportData.quantity / selectedOrder.plannedQuantity) * 100;

    // 建立報工記錄
    const now = new Date();
    const newReport: ProductionReport = {
      id: `PR-${Date.now()}`,
      subWorkOrderId: selectedOrder.id,
      subWorkOrderName: selectedOrder.id,
      processCode: selectedOrder.processCode,
      processName: selectedOrder.processName,
      plannedQuantity: selectedOrder.plannedQuantity,
      reportedQuantity: reportData.quantity,
      goodQuantity: reportData.goodQuantity,
      defectQuantity: reportData.defectQuantity,
      workHours: reportData.workHours,
      reportedBy: 'E001', // TODO: 從登入使用者取得
      reportedByName: '測試人員', // TODO: 從登入使用者取得
      reportDate: now.toISOString().slice(0, 10), // YYYY-MM-DD
      reportTime: now.toISOString(),
      notes: reportData.notes,
      yieldRate,
      achievementRate,
      createdAt: now.toISOString(),
    };

    // 儲存報工記錄（會自動更新子工單的 actualQuantity）
    addProductionReport(newReport);

    // 顯示成功畫面
    setShowSuccess(true);
    setReportStep('confirm');

    // 成功提示
    toast.success(
      '報工成功',
      `產出 ${reportData.quantity} 件（良品 ${reportData.goodQuantity} 件）· 良率 ${yieldRate.toFixed(1)}%`,
      4000
    );

    // 3 秒後重置
    setTimeout(() => {
      setShowSuccess(false);
      setReportStep('select');
      setSelectedOrder(null);
      // 重置表單資料
      setReportData({
        quantity: 0,
        goodQuantity: 0,
        defectQuantity: 0,
        workHours: 8,
        notes: '',
      });
    }, 3000);
  };

  // 計算達成率
  const calculateAchievementRate = () => {
    if (!selectedOrder) return 0;
    return Math.round((reportData.quantity / selectedOrder.plannedQuantity) * 100);
  };

  // 計算良率
  const calculateYieldRate = () => {
    if (reportData.quantity === 0) return 100;
    return Math.round((reportData.goodQuantity / reportData.quantity) * 100);
  };

  return (
    <div className="space-y-4">
      {/* 標題與控制列 */}
      <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between">
          {/* 左側：標題 */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <span className="text-white text-2xl">📊</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">生產報工</h2>
              <p className="text-sm text-slate-600">每日產出回報與工時記錄</p>
            </div>
          </div>

          {/* 右側：統計 */}
          <div className="flex items-center gap-3">
            <div className="px-4 py-2 bg-white/70 border-2 border-indigo-300 rounded-lg">
              <span className="text-xs font-bold text-indigo-700">進行中工單</span>
              <div className="text-2xl font-black text-indigo-600">{activeOrders.length}</div>
            </div>
            <div className="px-4 py-2 bg-white/70 border-2 border-green-300 rounded-lg">
              <span className="text-xs font-bold text-green-700">今日報工</span>
              <div className="text-2xl font-black text-green-600">{todayReportsCount}</div>
            </div>
          </div>
        </div>

        {/* 進度指示器 */}
        <div className="mt-4 flex items-center gap-2">
          {(['select', 'report', 'confirm'] as ReportStep[]).map((step, index) => (
            <div key={step} className="flex items-center">
              {/* 步驟圈 */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  reportStep === step
                    ? 'bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/30 scale-110'
                    : reportStep === 'confirm' && index < 2
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {reportStep === 'confirm' && index < 2 ? '✓' : index + 1}
              </div>
              {/* 連接線 */}
              {index < 2 && (
                <div
                  className={`w-16 h-1 mx-2 rounded-full transition-all ${
                    (reportStep === 'report' && index === 0) || reportStep === 'confirm'
                      ? 'bg-gradient-to-r from-indigo-500 to-violet-500'
                      : 'bg-slate-200'
                  }`}
                />
              )}
            </div>
          ))}
          <div className="ml-4 text-sm font-semibold text-slate-600">
            {reportStep === 'select' && '選擇工單'}
            {reportStep === 'report' && '填寫報工'}
            {reportStep === 'confirm' && '完成確認'}
          </div>
        </div>
      </div>

      {/* 步驟 1: 選擇工單 */}
      {reportStep === 'select' && (
        <div className="space-y-4">
          {/* 快速操作區 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 進行中工單 */}
            <button
              onClick={() => setSelectView('active')}
              className={`bg-gradient-to-br rounded-xl p-5 text-left hover:shadow-lg transition-all active:scale-95 ${
                selectView === 'active'
                  ? 'from-amber-100 to-orange-100 border-2 border-amber-400 shadow-amber-500/20'
                  : 'from-amber-50 to-orange-50 border-2 border-amber-300 hover:shadow-amber-500/20'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">⚡</span>
                <div>
                  <div className={`text-lg font-bold ${selectView === 'active' ? 'text-amber-900' : 'text-amber-800'}`}>進行中工單</div>
                  <div className="text-sm text-amber-600">{activeOrders.length} 筆待報工</div>
                </div>
              </div>
            </button>

            {/* 我的工單 */}
            <button
              onClick={() => setSelectView('my')}
              className={`bg-gradient-to-br rounded-xl p-5 text-left hover:shadow-lg transition-all active:scale-95 ${
                selectView === 'my'
                  ? 'from-blue-100 to-indigo-100 border-2 border-blue-400 shadow-blue-500/20'
                  : 'from-blue-50 to-indigo-50 border-2 border-blue-300 hover:shadow-blue-500/20'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">👤</span>
                <div>
                  <div className={`text-lg font-bold ${selectView === 'my' ? 'text-blue-900' : 'text-blue-800'}`}>我的工單</div>
                  <div className="text-sm text-blue-600">{myOrders.length} 筆指派</div>
                </div>
              </div>
            </button>

            {/* 今日已報工 */}
            <button
              onClick={() => setSelectView('completed')}
              className={`bg-gradient-to-br rounded-xl p-5 text-left hover:shadow-lg transition-all active:scale-95 ${
                selectView === 'completed'
                  ? 'from-green-100 to-emerald-100 border-2 border-green-400 shadow-green-500/20'
                  : 'from-green-50 to-emerald-50 border-2 border-green-300 hover:shadow-green-500/20'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">✅</span>
                <div>
                  <div className={`text-lg font-bold ${selectView === 'completed' ? 'text-green-900' : 'text-green-800'}`}>今日已報工</div>
                  <div className="text-sm text-green-600">{todayReportsList.length} 筆記錄</div>
                </div>
              </div>
            </button>
          </div>

          {/* 內容列表 */}
          <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
            {/* 工單列表視圖 (進行中工單 / 我的工單) */}
            {selectView !== 'completed' && (
              <>
                <div className="bg-gradient-to-r from-slate-50 to-white border-b-2 border-slate-200 px-6 py-4">
                  <h3 className="text-lg font-bold text-slate-800">
                    {selectView === 'my' ? '我的工單列表' : '待報工工單列表'}
                  </h3>
                </div>

                {displayOrders.length === 0 ? (
                  <div className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-xl p-12 text-center shadow-sm">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {selectView === 'my' ? '暫無指派給您的工單' : '暫無進行中的工單'}
                    </h3>
                    <p className="text-slate-600 mb-6 max-w-md mx-auto">
                      {selectView === 'my'
                        ? '您目前沒有指派的工單。請聯繫主管或切換至其他視圖查看。'
                        : '目前沒有進行中的工單需要報工。請聯繫主管派發工單或前往甘特圖查看排程。'}
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => setSelectView('active')}
                        className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
                      >
                        查看所有工單
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {displayOrders.map((order) => {
                      const mainOrder = getMainOrder(order.id);
                      const parentOrder = mainOrder ? orders.find(o => o.id === mainOrder.orderId) : null;
                      const achievementRate = Math.round((order.actualQuantity / order.plannedQuantity) * 100);

                      return (
                        <button
                          key={order.id}
                          onClick={() => handleSelectOrder(order)}
                          className="w-full p-5 hover:bg-indigo-50/50 transition-all text-left group"
                        >
                          <div className="flex items-center justify-between">
                            {/* 左側資訊 */}
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-lg font-bold text-indigo-600 group-hover:text-indigo-700">
                                  {order.id}
                                </span>
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-semibold border border-indigo-200">
                                  {order.processName}
                                </span>
                              </div>
                              <div className="text-sm text-slate-600 space-y-1">
                                <p>產品：{parentOrder?.productName || 'N/A'}</p>
                                <p>派發數量：{order.plannedQuantity.toLocaleString()}</p>
                              </div>
                            </div>

                            {/* 右側：進度與人員 */}
                            <div className="text-right">
                              <div className="mb-2">
                                <div className="text-sm text-slate-500 mb-1">生產進度</div>
                                <div className="flex items-center gap-2">
                                  <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full transition-all ${
                                        achievementRate >= 100
                                          ? 'bg-gradient-to-r from-green-500 to-emerald-400'
                                          : achievementRate >= 50
                                          ? 'bg-gradient-to-r from-blue-500 to-cyan-400'
                                          : 'bg-gradient-to-r from-amber-500 to-orange-400'
                                      }`}
                                      style={{ width: `${Math.min(achievementRate, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-bold text-slate-700">{achievementRate}%</span>
                                </div>
                              </div>
                              <div className="text-xs text-slate-500">
                                已產出：{order.actualQuantity.toLocaleString()} / {order.plannedQuantity.toLocaleString()}
                              </div>
                            </div>

                            {/* 箭頭 */}
                            <div className="ml-4 text-slate-400 group-hover:text-indigo-500 group-hover:scale-110 transition-all">
                              →
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* 今日報工記錄視圖 */}
            {selectView === 'completed' && (
              <>
                <div className="bg-gradient-to-r from-green-50 to-white border-b-2 border-green-200 px-6 py-4">
                  <h3 className="text-lg font-bold text-green-800">今日報工記錄</h3>
                </div>

                {todayReportsList.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="text-6xl mb-4">📝</div>
                    <p className="text-slate-500 text-lg">今日尚無報工記錄</p>
                    <p className="text-sm text-slate-400 mt-2">完成生產後請填寫報工資料</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {todayReportsList.map((report) => {
                      const subOrder = subWorkOrders.find(o => o.id === report.subWorkOrderId);
                      const achievementRate = Math.round(report.achievementRate);

                      return (
                        <div key={report.id} className="p-5 hover:bg-green-50/30 transition-all">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-lg font-bold text-green-700">{report.subWorkOrderName}</span>
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold border border-green-200">
                                  {report.processName}
                                </span>
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold border border-blue-200">
                                  良率 {report.yieldRate.toFixed(1)}%
                                </span>
                              </div>
                              <div className="text-sm text-slate-600 space-y-1">
                                <p>報工數量：{report.reportedQuantity} 件（良品 {report.goodQuantity} / 不良品 {report.defectQuantity}）</p>
                                <p>工作時數：{report.workHours} 小時</p>
                                {report.notes && <p>備註：{report.notes}</p>}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-slate-500">
                                {new Date(report.reportTime).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* 步驟 2: 填寫報工 */}
      {reportStep === 'report' && selectedOrder && (
        <div className="max-w-4xl mx-auto">
          {/* 工單資訊卡片 */}
          <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-200 rounded-xl p-6 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-800">{selectedOrder.id}</h3>
                <p className="text-sm text-slate-600">{selectedOrder.processName}</p>
              </div>
              <button
                onClick={() => setReportStep('select')}
                className="px-4 py-2 bg-white/70 border-2 border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all active:scale-95"
              >
                ← 返回
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500">計劃數量</span>
                <div className="text-lg font-bold text-slate-800">{selectedOrder.plannedQuantity.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-slate-500">已產出</span>
                <div className="text-lg font-bold text-indigo-600">{selectedOrder.actualQuantity.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-slate-500">剩餘</span>
                <div className="text-lg font-bold text-amber-600">
                  {(selectedOrder.plannedQuantity - selectedOrder.actualQuantity).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* 報工表單 */}
          <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-white border-b-2 border-indigo-200 px-6 py-4">
              <h3 className="text-lg font-bold text-slate-800">填寫生產數據</h3>
            </div>

            <div className="p-6 space-y-6">
              {/* 產出數量區 */}
              <div className="space-y-4">
                <h4 className="text-md font-bold text-slate-700 flex items-center gap-2">
                  <span className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg flex items-center justify-center text-white text-xs">
                    1
                  </span>
                  產出數量
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 總產出 */}
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      總產出數量
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={reportData.quantity}
                      onChange={(e) => handleQuantityChange('quantity', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white border-2 border-slate-300 rounded-lg text-2xl font-bold text-slate-800 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all"
                      placeholder="0"
                    />
                    <p className="text-xs text-slate-500 mt-2">良品 + 不良品</p>
                  </div>

                  {/* 良品數 */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-green-700 mb-2">
                      良品數量 ✓
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={reportData.goodQuantity}
                      onChange={(e) => handleQuantityChange('goodQuantity', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white border-2 border-green-300 rounded-lg text-2xl font-bold text-green-600 focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all"
                      placeholder="0"
                    />
                    <p className="text-xs text-green-600 mt-2">合格品數量</p>
                  </div>

                  {/* 不良品數 */}
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-300 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-red-700 mb-2">
                      不良品數 ✕
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={reportData.defectQuantity}
                      onChange={(e) => handleQuantityChange('defectQuantity', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white border-2 border-red-300 rounded-lg text-2xl font-bold text-red-600 focus:ring-2 focus:ring-red-400 focus:border-red-400 transition-all"
                      placeholder="0"
                    />
                    <p className="text-xs text-red-600 mt-2">不合格品數量</p>
                  </div>
                </div>

                {/* 實時統計 */}
                <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-xl border-2 border-indigo-200">
                  <div className="flex-1 text-center">
                    <div className="text-sm text-indigo-600 font-semibold">達成率</div>
                    <div className="text-2xl font-black text-indigo-700">{calculateAchievementRate()}%</div>
                  </div>
                  <div className="w-px h-10 bg-indigo-300"></div>
                  <div className="flex-1 text-center">
                    <div className="text-sm text-green-600 font-semibold">良率</div>
                    <div className="text-2xl font-black text-green-700">{calculateYieldRate()}%</div>
                  </div>
                </div>
              </div>

              {/* 工時記錄 */}
              <div className="space-y-4">
                <h4 className="text-md font-bold text-slate-700 flex items-center gap-2">
                  <span className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg flex items-center justify-center text-white text-xs">
                    2
                  </span>
                  工時記錄
                </h4>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-amber-700 mb-2">
                    今日工作時數（小時）
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      value={reportData.workHours}
                      onChange={(e) => setReportData({ ...reportData, workHours: parseFloat(e.target.value) || 0 })}
                      className="flex-1 px-4 py-3 bg-white border-2 border-amber-300 rounded-lg text-xl font-bold text-amber-700 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all"
                    />
                    <div className="flex gap-2">
                      {[4, 8, 12].map((hours) => (
                        <button
                          key={hours}
                          onClick={() => setReportData({ ...reportData, workHours: hours })}
                          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                            reportData.workHours === hours
                              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                              : 'bg-white border-2 border-amber-300 text-amber-700 hover:bg-amber-100'
                          }`}
                        >
                          {hours}h
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 備註 */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  備註（選填）
                </label>
                <textarea
                  value={reportData.notes}
                  onChange={(e) => setReportData({ ...reportData, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all"
                  rows={3}
                  placeholder="請輸入異常狀況、機台問題、或其他需要說明的事項..."
                />
              </div>
            </div>

            {/* 提交按鈕 */}
            <div className="bg-slate-50 border-t-2 border-slate-200 p-6">
              <button
                onClick={handleSubmitReport}
                disabled={reportData.quantity === 0}
                className={`w-full py-4 rounded-xl text-lg font-bold transition-all shadow-lg ${
                  reportData.quantity === 0
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-violet-500 text-white hover:from-indigo-700 hover:to-violet-600 shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 active:scale-95'
                }`}
              >
                {reportData.quantity === 0 ? '請輸入產出數量' : '✓ 確認報工'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 步驟 3: 完成確認 */}
      {reportStep === 'confirm' && (
        <div className="max-w-2xl mx-auto">
          {showSuccess ? (
            <div className="bg-white border-2 border-green-200 rounded-xl shadow-lg p-12 text-center animate-fade-in">
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-green-500/30">
                <span className="text-white text-5xl">✓</span>
              </div>
              <h3 className="text-3xl font-bold text-green-600 mb-2">報工成功！</h3>
              <p className="text-slate-600 mb-6">感謝您的辛苦工作</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4">
                  <div className="text-sm text-green-600 font-semibold">本日產出</div>
                  <div className="text-3xl font-black text-green-700">{reportData.quantity}</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4">
                  <div className="text-sm text-blue-600 font-semibold">工作時數</div>
                  <div className="text-3xl font-black text-blue-700">{reportData.workHours}h</div>
                </div>
              </div>
              <p className="text-sm text-slate-500">3 秒後自動返回...</p>
            </div>
          ) : (
            <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-slate-800 mb-6">報工確認</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-slate-50 rounded-xl">
                  <span className="text-slate-600">工單編號</span>
                  <span className="font-bold text-slate-800">{selectedOrder?.id}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-slate-50 rounded-xl">
                  <span className="text-slate-600">總產出</span>
                  <span className="font-bold text-slate-800">{reportData.quantity}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                  <span className="text-green-600">良品數</span>
                  <span className="font-bold text-green-700">{reportData.goodQuantity}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-red-50 rounded-xl">
                  <span className="text-red-600">不良品數</span>
                  <span className="font-bold text-red-700">{reportData.defectQuantity}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-amber-50 rounded-xl">
                  <span className="text-amber-600">工作時數</span>
                  <span className="font-bold text-amber-700">{reportData.workHours} 小時</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
